import type { PickableStorefrontPage } from "@loot-os/api-contract";
import { cn } from "@loot-os/ui/lib/utils";
import type { ReactNode } from "react";

import { type LayoutSection, layoutSections } from "../lib/storefrontPages";

/** A block of text or a control. */
const LINE = "rounded-[1px] bg-current opacity-30";
/** A card or a piece of art, stronger than text. */
const FILL = "rounded-[2px] bg-current opacity-45";

/** `count` equal blocks in a row, filling the height they are given. */
function Row({ count, className }: { count: number; className?: string }) {
  return (
    <div className="flex min-h-0 flex-1 gap-[3px]">
      {Array.from({ length: count }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: identical blocks in fixed positions
        <div key={index} className={cn(FILL, "flex-1", className)} />
      ))}
    </div>
  );
}

/** A section's title line over its content, the way most of Home's rows open. */
function Titled({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[3px]">
      <div className={cn(LINE, "h-[3px] w-1/4 shrink-0")} />
      {children}
    </div>
  );
}

/**
 * One Home section in its look, and its share of the page's height: the hero largest, rows of
 * cards next, a band of text least. Unknown pairs draw as a line, so a new look never breaks it.
 */
function homeSection(section: LayoutSection): { weight: number; body: ReactNode } {
  const key = `${section.id}:${section.variantId}`;
  switch (key) {
    case "hero:split":
      return {
        weight: 6,
        body: (
          <div className="flex min-h-0 flex-1 gap-1.5">
            <div className="flex flex-1 flex-col justify-center gap-[3px]">
              <div className={cn(LINE, "h-1.5 w-11/12 opacity-50")} />
              <div className={cn(LINE, "h-1.5 w-2/3 opacity-50")} />
              <div className={cn(LINE, "h-[3px] w-3/4")} />
              <div className={cn(FILL, "mt-0.5 h-1.5 w-1/3")} />
            </div>
            <div className={cn(FILL, "w-2/5")} />
          </div>
        ),
      };
    case "hero:centered":
      return {
        weight: 6,
        body: (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[3px]">
            <div className={cn(LINE, "h-1.5 w-1/2 opacity-50")} />
            <div className={cn(LINE, "h-[3px] w-1/3")} />
            <div className={cn(FILL, "mt-0.5 h-1.5 w-1/5")} />
          </div>
        ),
      };
    case "hero:carousel":
      return {
        weight: 6,
        body: (
          <div className="flex min-h-0 flex-1 flex-col gap-[3px]">
            <div className={cn(FILL, "flex-1 opacity-55")} />
            <div className="flex shrink-0 justify-center gap-[2px]">
              {[0, 1, 2].map((dot) => (
                <div key={dot} className={cn(LINE, "size-[3px] rounded-full")} />
              ))}
            </div>
          </div>
        ),
      };
    case "liveHits:strip":
    case "recentOpens:compact":
      return { weight: 1, body: <Row count={8} className="opacity-30" /> };
    case "liveHits:cards":
    case "recentOpens:cards":
      return {
        weight: 2.5,
        body: (
          <Titled>
            <Row count={7} />
          </Titled>
        ),
      };
    case "featuredBoxes:grid":
      return {
        weight: 5,
        body: (
          <Titled>
            <Row count={4} />
            <Row count={4} />
          </Titled>
        ),
      };
    case "featuredBoxes:large":
      return {
        weight: 5,
        body: (
          <Titled>
            <Row count={2} />
          </Titled>
        ),
      };
    case "featuredBoxes:rail":
      return {
        weight: 3.5,
        body: (
          <Titled>
            <Row count={5} />
          </Titled>
        ),
      };
    case "battles:grid":
      return {
        weight: 3,
        body: (
          <Titled>
            <Row count={3} />
          </Titled>
        ),
      };
    case "battles:rows":
      return {
        weight: 3,
        body: (
          <Titled>
            <Row count={1} className="opacity-35" />
            <Row count={1} className="opacity-35" />
            <Row count={1} className="opacity-35" />
          </Titled>
        ),
      };
    case "forge:showcase":
      return {
        weight: 3,
        body: (
          <Titled>
            <Row count={4} />
          </Titled>
        ),
      };
    case "forge:stage":
      return { weight: 3.5, body: <div className={cn(FILL, "min-h-0 flex-1 opacity-55")} /> };
    case "forge:band":
      return { weight: 1.5, body: <div className={cn(FILL, "min-h-0 flex-1 opacity-35")} /> };
    case "promoCards:row":
    case "reviews:grid":
      return { weight: 1.5, body: <Row count={3} className="opacity-35" /> };
    case "products:grid":
      return {
        weight: 2.5,
        body: (
          <Titled>
            <Row count={4} />
          </Titled>
        ),
      };
    case "promises:grid":
      return { weight: 1, body: <Row count={3} className="opacity-25" /> };
    case "faq:split":
      return {
        weight: 2.5,
        body: (
          <div className="flex min-h-0 flex-1 gap-1.5">
            <div className={cn(LINE, "h-1.5 w-1/4 opacity-50")} />
            <Lines />
          </div>
        ),
      };
    case "faq:centered":
      return {
        weight: 2.5,
        body: (
          <div className="flex min-h-0 flex-1 flex-col items-center gap-[3px] px-[15%]">
            <div className={cn(LINE, "h-1.5 w-1/3 shrink-0 opacity-50")} />
            <Lines />
          </div>
        ),
      };
    case "faq:stacked":
      return {
        weight: 2.5,
        body: (
          <Titled>
            <Lines />
          </Titled>
        ),
      };
    case "closing:band":
      return {
        weight: 1.5,
        body: (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[3px]">
            <div className={cn(LINE, "h-1 w-1/3")} />
            <div className={cn(FILL, "h-1.5 w-1/6")} />
          </div>
        ),
      };
    default:
      return { weight: 1, body: <div className={cn(LINE, "h-[3px] w-3/4")} /> };
  }
}

/** The FAQ's questions, as rules filling the height they are given. */
function Lines() {
  return (
    <div className="flex min-h-0 flex-1 flex-col justify-around">
      {[0, 1, 2, 3].map((line) => (
        <div key={line} className={cn(LINE, "h-px w-full")} />
      ))}
    </div>
  );
}

/** The box page's heading: its breadcrumb, name, price and the buy row. */
function BoxHeading() {
  return (
    <div className="flex flex-col gap-[3px]">
      <div className={cn(LINE, "h-[3px] w-1/4")} />
      <div className={cn(LINE, "h-2 w-3/4 opacity-50")} />
      <div className={cn(LINE, "h-1 w-1/3")} />
      <div className="mt-0.5 flex gap-[3px]">
        <div className={cn(FILL, "h-2 w-1/4 opacity-30")} />
        <div className={cn(FILL, "h-2 w-1/3")} />
      </div>
    </div>
  );
}

/** The box page's contents: the odds bar over the items. */
function BoxContents() {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[3px]">
      <div className={cn(LINE, "h-1 w-full shrink-0 opacity-45")} />
      <Row count={6} />
      <Row count={6} />
    </div>
  );
}

/** The battles lobby in a layout: showroom's chips over cards three across, arcade's toolbar over rows. */
function BattlesLobby({ layout }: { layout: string }) {
  const arcade = layout === "arcade";
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[3px]">
      <div className="flex shrink-0 items-center gap-[3px]">
        {arcade && <div className={cn(FILL, "size-2")} />}
        <div className={cn(LINE, "h-2 w-1/3 opacity-50")} />
        {!arcade && <div className={cn(FILL, "ml-auto h-2 w-1/5")} />}
      </div>
      {arcade ? (
        <>
          <div className="flex shrink-0 gap-[3px]">
            <div className={cn(FILL, "h-2 w-1/6 opacity-30")} />
            <div className={cn(FILL, "h-2 w-1/6 opacity-30")} />
            <div className={cn(FILL, "ml-auto h-2 w-1/4")} />
          </div>
          {["a", "b", "c", "d"].map((key) => (
            <div key={key} className={cn(FILL, "min-h-0 flex-1")} />
          ))}
        </>
      ) : (
        <>
          <div className="flex shrink-0 gap-[3px]">
            <div className={cn(LINE, "h-1.5 w-1/6")} />
            <div className={cn(LINE, "h-1.5 w-1/6")} />
          </div>
          <Row count={3} />
          <Row count={3} />
        </>
      )}
    </div>
  );
}

/** Building a battle: showroom's two panels, or arcade's Create at the top over cards and a column. */
function BattleCreate({ layout }: { layout: string }) {
  const arcade = layout === "arcade";
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-[3px]">
      <div className="flex shrink-0 items-center gap-[3px]">
        <div className={cn(LINE, "h-2 w-1/3 opacity-50")} />
        {arcade && <div className={cn(FILL, "ml-auto h-2 w-1/3")} />}
      </div>
      <div className="flex min-h-0 flex-1 gap-1.5">
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-[3px]",
            !arcade && "rounded-[2px] bg-current/10 p-[3px]",
          )}
        >
          {arcade ? (
            <>
              <Row count={4} />
              <Row count={4} />
            </>
          ) : (
            <Row count={3} />
          )}
        </div>
        <div className="flex w-1/3 min-h-0 flex-col gap-[3px]">
          {(arcade ? ["a", "b", "c", "d", "e"] : ["a", "b", "c"]).map((key) => (
            <div key={key} className={cn(arcade ? FILL : LINE, "min-h-0 flex-1 opacity-40")} />
          ))}
          {!arcade && <div className={cn(FILL, "h-2 shrink-0")} />}
        </div>
      </div>
    </div>
  );
}

/**
 * A layout of a page as an outline of the page, nav to footer: Home's drawn from the sections its
 * list places, each in its look and with its share of the height (so a new layout draws itself),
 * the box page's and the battles lobby's arrangements by hand.
 */
export function PageLayoutSketch({
  page,
  layout,
  className,
}: {
  page: PickableStorefrontPage;
  layout: string;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn(
        "flex h-40 w-full flex-col gap-[3px] overflow-hidden rounded-md bg-muted p-1.5",
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-1">
        <div className={cn(FILL, "size-[5px]")} />
        <div className={cn(LINE, "h-[3px] w-6")} />
        <div className={cn(LINE, "ml-auto h-[3px] w-4")} />
      </div>
      {page === "home" ? (
        layoutSections(page, layout).map((section) => {
          const { weight, body } = homeSection(section);
          return (
            <div
              key={section.id}
              className="flex min-h-0 basis-0 flex-col"
              style={{ flexGrow: weight }}
            >
              {body}
            </div>
          );
        })
      ) : page === "battles" ? (
        <BattlesLobby layout={layout} />
      ) : page === "battleCreate" ? (
        <BattleCreate layout={layout} />
      ) : layout === "split" ? (
        <>
          <div className="flex min-h-0 basis-0 gap-1.5" style={{ flexGrow: 5 }}>
            <div className={cn(FILL, "w-2/5 opacity-55")} />
            <div className="flex-1 pt-1">
              <BoxHeading />
            </div>
          </div>
          <div className="flex min-h-0 basis-0" style={{ flexGrow: 4 }}>
            <BoxContents />
          </div>
        </>
      ) : (
        <>
          <div className="shrink-0">
            <BoxHeading />
          </div>
          <div className={cn(FILL, "min-h-0 basis-0 opacity-55")} style={{ flexGrow: 4 }} />
          <div className="flex min-h-0 basis-0" style={{ flexGrow: 4 }}>
            <BoxContents />
          </div>
        </>
      )}
      <div className={cn(LINE, "h-1 w-full shrink-0 opacity-20")} />
    </div>
  );
}
